import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  DIALOG_MODE_LOADING,
  DIALOG_MODE_STANDARD_BUY,
  FETCH_MODE_EAGER,
  FETCH_MODE_LAZY,
  NOT_FOUND,
  DISCONTINUED,
} from '../constants';
import { ProductInfoDialog } from '../containers/ProductInfoDialog';
import { shouldShowIsDiscontinuedErrorInfo } from '../utils/shouldShowIsDiscontinuedErrorInfo';
import type { UseProductInfoDialogProps } from './types';
import { useGetProductInfoDialogProducts } from './useGetProductInfoDialogProducts';
import { useGetProductInfoDialogShowMrpStatement } from './useGetProductInfoDialogShowMrpStatement';

export const useProductInfoDialog = ({
  atpClusterId,
  currentLanguage,
  customer,
  dialog,
  fetchMode = FETCH_MODE_LAZY,
  globalApiUrl,
  graphqlUrl,
  mode = DIALOG_MODE_STANDARD_BUY,
  productCodes,
  shared,
  staticApiUrl,
  tenant,
  token,
  ...props
}: UseProductInfoDialogProps) => {
  const { onClose, ...dialogProps } = dialog ?? {};
  const { customerId } = customer ?? {};
  const {
    data: products,
    fetch: fetchProducts,
    isLoaded: areProductsLoaded,
    isLoading: areProductsLoading,
  } = useGetProductInfoDialogProducts({
    atpClusterId,
    globalApiUrl,
    currentLanguage,
    customerId,
    graphqlUrl,
    productCodes,
    tenant,
    token,
  });
  const {
    data: showMrpStatement,
    fetch: fetchShowMrpStatement,
    isLoaded: isShowMrpStatementLoaded,
    isLoading: isShowMrpStatementLoading,
  } = useGetProductInfoDialogShowMrpStatement({
    staticApiUrl,
    tenant,
  });

  const [isOpen, setIsOpen] = useState(false);
  const isLoaded = isShowMrpStatementLoaded && areProductsLoaded;
  const isLoading = isShowMrpStatementLoading || areProductsLoading;
  const fetchData = useCallback(() => {
    if (!isLoaded) {
      fetchProducts();
      fetchShowMrpStatement();
    }
  }, [fetchProducts, fetchShowMrpStatement, isLoaded]);
  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);
  const open = useCallback(() => {
    setIsOpen(true);
    if (fetchMode === FETCH_MODE_LAZY) {
      fetchData();
    }
  }, [fetchData, fetchMode]);
  useEffect(() => {
    if (fetchMode === FETCH_MODE_EAGER) {
      fetchData();
    }
  }, [fetchData, fetchMode]);

  const dialogMode = useMemo(() => {
    if (isLoading) {
      return DIALOG_MODE_LOADING;
    } else if (products.length === 0) {
      return NOT_FOUND;
    } else if (shouldShowIsDiscontinuedErrorInfo(products)) {
      return DISCONTINUED;
    }

    return mode;
  }, [isLoading, mode, products]);

  return useMemo(
    () => ({
      closeProductInfoDialog: close,
      isProductInfoDialogOpen: isOpen,
      openProductInfoDialog: open,
      productInfoDialog: (
        <ProductInfoDialog
          {...props}
          tenant={tenant}
          staticApiUrl={staticApiUrl}
          customer={customer}
          dialog={{ ...dialogProps, onClose: close, open: isOpen }}
          mode={dialogMode}
          products={products}
          shared={{ ...shared, showMrpStatement }}
        />
      ),
    }),
    [
      close,
      isOpen,
      open,
      props,
      tenant,
      staticApiUrl,
      customer,
      dialogProps,
      dialogMode,
      products,
      shared,
      showMrpStatement,
    ],
  );
};
